import { useCallback, useEffect, useRef, useState } from 'react';
import {
  MENU_LAYOUT_COLUMNS,
  MENU_LAYOUT_ROWS,
  menuColStartFromPointer,
  menuRowStartFromPointer,
  normalizeTabPlacement,
} from '../utils/menuLayout';

const DRAG_THRESHOLD = 4;

/**
 * Glisser-déposer logo / onglets sur la barre menu (grille 48 cols), vue éditeur desktop.
 */
export function useMenuBarLayoutDrag({ enabled, gridRef, onPlacementChange }) {
  const sessionRef = useRef(null);
  const suppressClickRef = useRef(false);
  const [draggingId, setDraggingId] = useState(null);

  const startLayoutDrag = useCallback(
    (e, targetId, placement) => {
      if (!enabled || e.button !== 0) return;
      const gridEl = gridRef.current;
      if (!gridEl) return;
      e.preventDefault();
      e.stopPropagation();
      const p = normalizeTabPlacement(placement);
      const rect = gridEl.getBoundingClientRect();
      const pointerCol =
        Math.floor(((e.clientX - rect.left) / rect.width) * MENU_LAYOUT_COLUMNS) + 1;
      const pointerRow =
        Math.floor(((e.clientY - rect.top) / rect.height) * MENU_LAYOUT_ROWS) + 1;
      sessionRef.current = {
        targetId,
        grabOffsetCol: pointerCol - p.colStart,
        grabOffsetRow: pointerRow - p.rowStart,
        colSpan: p.colSpan,
        rowSpan: p.rowSpan,
        align: p.align,
        startX: e.clientX,
        startY: e.clientY,
        moved: false,
      };
      setDraggingId(targetId);
    },
    [enabled, gridRef],
  );

  useEffect(() => {
    if (!enabled) return undefined;

    const onMove = (e) => {
      const s = sessionRef.current;
      if (!s) return;
      if (
        !s.moved &&
        Math.hypot(e.clientX - s.startX, e.clientY - s.startY) < DRAG_THRESHOLD
      ) {
        return;
      }
      s.moved = true;
      const gridEl = gridRef.current;
      if (!gridEl) return;
      const colStart = menuColStartFromPointer(
        e.clientX,
        gridEl,
        s.colSpan,
        s.grabOffsetCol,
      );
      const rowStart = menuRowStartFromPointer(
        e.clientY,
        gridEl,
        s.rowSpan,
        s.grabOffsetRow,
      );
      onPlacementChange(s.targetId, {
        colStart,
        colSpan: s.colSpan,
        rowStart,
        rowSpan: s.rowSpan,
        align: s.align,
      });
    };

    const onUp = () => {
      if (sessionRef.current?.moved) {
        suppressClickRef.current = true;
        window.setTimeout(() => {
          suppressClickRef.current = false;
        }, 0);
      }
      sessionRef.current = null;
      setDraggingId(null);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [enabled, gridRef, onPlacementChange]);

  return { startLayoutDrag, draggingId, suppressClickRef };
}
